import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { stripePromise } from '../../utils/stripe';
import { recallUpsellToken } from '../../utils/upsellToken';
import { useNetwork } from '../../utils/network';
import { useShowSnackbar } from '../../utils/snackbar';
import { formatAud, plural } from './promoPack';
import { CYAN, GOLD, INK, BODY, MUTED, LINE, kickerSx, ctaSx, panelSx } from '../GiveawayOffer/giveawayOfferTheme';

/* The one-time offer after a promo pack is paid for — the same offer, on the
 * same terms, as the giveaway site's /giveaway/upsell and the member portal's
 * modal: the pack's `Upsell` option (extra entries and cards for a bit more),
 * charged in one click on the card that just paid. Shown only when the flow
 * would show it there — the pack has no bonus, or the bonus was added — and
 * only while the server still holds the short-lived token for this purchase.
 *
 * THE CHARGE IS THE GIVEAWAY SITE'S, LINE FOR LINE: `GiveawayExclusiveOffer`'s
 * handleYes (elements.submit, POST /checkout/upsell-payment-intent with the
 * token, stripe.confirmPayment with the client secret, redirect only if
 * required), inside its own plain <Elements> as /giveaway/upsell mounts it.
 * Only the surface differs: this one is dark, for the promo dialog, and hands
 * what was added back to the caller instead of writing sessionStorage.
 *
 * `onDone(applied)`: null when declined, unavailable or failed; the extras and
 * price when the charge went through. Either way the purchase before it has
 * already succeeded — nothing here can undo that. */

export type UpsellApplied = { extraEntries: number; extraCards: number; price: number };

type UpsellPackage = {
  id: string | number;
  cards: number;
  entries: number;
  Upsell: { price: string; extra_cards: number; extra_entries: number };
};

const Offer = ({
  paymentIntentId,
  pkg,
  onDone
}: {
  paymentIntentId: string;
  pkg: UpsellPackage;
  onDone: (applied: UpsellApplied | null) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { showSnackbar } = useShowSnackbar();
  const { post } = useNetwork();
  const [loading, setLoading] = useState(false);

  const extraEntries = Number(pkg?.Upsell?.extra_entries ?? 0);
  const extraCards = Number(pkg?.Upsell?.extra_cards ?? 0);
  const price = Number(pkg?.Upsell?.price ?? 0);

  const handleYes = async () => {
    if (!stripe || !elements) {
      showSnackbar('Stripe has failed to load.', 'error');
      return;
    }

    setLoading(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showSnackbar('Failed to submit payment details.', 'error');
        setLoading(false);
        return;
      }

      const result = await post(
        `/checkout/upsell-payment-intent`,
        // the charge is authorised by the token, not by knowing the intent id
        { upsellToken: recallUpsellToken(paymentIntentId) },
        true
      );

      const { error } = await stripe.confirmPayment({
        clientSecret: result.clientSecret,
        confirmParams: { return_url: `${window.location}` },
        redirect: 'if_required'
      });

      if (error) {
        showSnackbar('An unexpected error occurred.', 'error');
      } else {
        showSnackbar('Payment successful!', 'success');
        onDone({ extraEntries, extraCards, price });
      }
    } catch (e) {
      showSnackbar('An unexpected error occurred.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ ...kickerSx, mb: 1.5 }}>One-time offer</Typography>
      <Typography
        component="h2"
        sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 900, lineHeight: 1.1, color: INK }}
      >
        Boost your entry?
      </Typography>
      <Typography sx={{ mt: 1.25, fontSize: { xs: 15, md: 16 }, lineHeight: 1.6, color: BODY }}>
        Your pack is paid for. Add this upgrade now, in one click on the same card — it is only
        offered right now.
      </Typography>

      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, minmax(0,1fr))' },
          gap: 1.5
        }}
      >
        {[
          [plural(extraEntries, 'extra giveaway entry', 'extra giveaway entries'), 'More entries, more chances.'],
          [plural(extraCards, 'extra Digital Collectible', 'extra Digital Collectibles'), 'Added to your pack.']
        ].map(([title, note]) => (
          <Box key={title} sx={{ ...panelSx, p: 2, textAlign: 'left' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: INK }}>+{title}</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13.5, color: MUTED }}>{note}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ mt: 3, fontSize: 18, fontWeight: 900, color: INK }}>
        Add it for <Box component="span" sx={{ color: GOLD }}>{formatAud(price)}</Box>
      </Typography>

      <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
        <Button onClick={handleYes} disabled={loading} sx={{ ...ctaSx, minWidth: 200 }}>
          {loading ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : 'Yes, boost my entry'}
        </Button>
        <Button
          onClick={() => onDone(null)}
          disabled={loading}
          sx={{
            minHeight: 48,
            px: 3,
            borderRadius: 999,
            fontWeight: 800,
            textTransform: 'none',
            color: INK,
            border: `1px solid ${LINE}`
          }}
        >
          No thanks
        </Button>
      </Box>
      <Typography sx={{ mt: 2, fontSize: 12.5, color: MUTED }}>
        Secure checkout powered by Stripe.
      </Typography>
    </Box>
  );
};

/* Asks the API what the offer is (404 = none, or the window has closed) and
 * shows it; with nothing to offer it is over at once. */
const PromoUpsellStep = ({
  paymentIntentId,
  onDone
}: {
  paymentIntentId: string;
  onDone: (applied: UpsellApplied | null) => void;
}) => {
  const { get } = useNetwork();
  const [pkg, setPkg] = useState<UpsellPackage | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const result = await get(`/checkout/upsell-details/${paymentIntentId}`, true);
        if (alive) setPkg(result?.upsellPackage ?? null);
      } catch {
        if (alive) setPkg(null);
      }
    })();
    return () => {
      alive = false;
    };
    // `get` is recreated each render; the offer is read once per payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId]);

  useEffect(() => {
    if (pkg === null) onDone(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg]);

  if (!pkg) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: CYAN }} aria-label="One moment" />
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ appearance: { theme: 'night' } }}>
      <Offer paymentIntentId={paymentIntentId} pkg={pkg} onDone={onDone} />
    </Elements>
  );
};

export default PromoUpsellStep;
